import { useRef, useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { authApi, tenantApi, coreApi, brandingApi, oauthApi } from "@/lib/api/client";
import type { OAuthProvider } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth";
import { safeRedirect, readPendingLoginReturn, clearPendingLoginReturn } from "@/lib/safe-redirect";
import { getConfig } from "@/lib/config";
import { getVersionDisplay } from "@/lib/version-display";
import { Card, Text, Title, Loader, Stack, Group, Box, Flex } from "@mantine/core";
import { LoginOptions } from "@/components/auth/login-options";
import { Building2, Shield, Wifi } from "lucide-react";

const config = getConfig();
const SIDECAR_URL = config.SIDECAR_BASE_URL;
const API_BASE = config.API_BASE_URL;

const AUTH_ERRORS: Record<string, string> = {
  oauth_denied: "Sign-in was cancelled.",
  oauth_no_account: "No account found for your email. Contact your admin.",
  oauth_expired: "Sign-in session expired. Please try again.",
  oauth_error: "Sign-in failed. Please try again.",
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, setAuth } = useAuthStore();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { data: ipData } = useQuery({
    queryKey: ["signed-ip-info"],
    queryFn: async () => {
      const response = await fetch(`${SIDECAR_URL}/signed-ip-info`, { mode: "cors" });
      if (!response.ok) return null;
      return response.json() as Promise<{ ip: string; token: string }>;
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const { data: branding } = useQuery({
    queryKey: ["branding", ipData?.token],
    queryFn: () => brandingApi.getBranding(ipData?.token ?? undefined).then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    initialData: config.LOGO_BASE64 || config.SITE_NAME ? {
      site_id: null,
      tenant_id: null,
      tenant_name: null,
      site_name: config.SITE_NAME || null,
      logo_url: null,
      logo_base64: config.LOGO_BASE64 || null,
      primary_color: null,
      secondary_color: null,
      color_scheme: null,
    } : undefined,
  });

  const { data: versionInfo, isError: versionError, isLoading: versionLoading } = useQuery({
    queryKey: ["core-version-info"],
    queryFn: ({ signal }) => coreApi.getVersion(signal).then((r) => r.data),
    staleTime: 60 * 1000,
    retry: 1,
  });
  const { data: providers = [], isSuccess: providersLoaded, isError: providersFailed } = useQuery({
    queryKey: ["oauth-providers"],
    queryFn: () => oauthApi.getProviders().then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const versionDisplay = getVersionDisplay(versionInfo?.backend_version, versionError, versionLoading);

  const authParam = searchParams.get("auth");
  const errorParam = searchParams.get("error");
  const errorEmail = searchParams.get("email");
  const errorProvider = searchParams.get("provider");
  const rawRedirect = searchParams.get("redirect");

  // Back to the page they asked for. Router state only survives a soft
  // redirect; a Slack link cold-loads in a new tab, so the remembered path
  // (written by ProtectedRoute on the way out) and `?redirect=` are checked
  // too. Same-origin relative paths only (no open redirect), query and hash kept.
  const pendingReturn = readPendingLoginReturn();
  const from = safeRedirect((location.state as { from?: string })?.from || rawRedirect || pendingReturn || "/");

  // The remembered return path is spent once this page resolves a target:
  // leaving it behind would resurrect an old page after a later login.
  useEffect(() => {
    if (pendingReturn) clearPendingLoginReturn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-redirect to OAuth provider if ?auth=<provider> (validated against known providers)
  // The hop leaves the page, so it fires once: a query settling would
  // otherwise re-run it with `from` re-resolved from storage — already spent
  // by the cleanup above — and send the member to the dashboard.
  const authRedirectFired = useRef(false);
  useEffect(() => {
    if (authRedirectFired.current) return;
    if (authParam && !isAuthenticated && providers.some((p) => p.provider === authParam)) {
      authRedirectFired.current = true;
      // Back to the doorbell call page in a Slack link (or any pending page):
      // the return path travels in the URL through the OAuth round-trip.
      window.location.href = `${API_BASE}/tenant-portal/oauth/${encodeURIComponent(authParam)}/?redirect=${encodeURIComponent(from)}`;
    }
  }, [authParam, isAuthenticated, from, providers]);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  // Show loading while auto-redirecting to provider (fall through to login form if provider unknown)
  if (authParam && (!providersLoaded || providers.some((p) => p.provider === authParam))) {
    return (
      <Flex align="center" justify="center" direction="column" gap="sm" mih="100vh" p={16} style={{ background: "var(--mantine-color-default-hover)" }}>
        <Loader size="sm" />
        <Text fz="sm" c="dimmed">Redirecting...</Text>
      </Flex>
    );
  }

  const handleOAuthSignIn = (provider: OAuthProvider) => {
    // `from` already resolved state -> ?redirect= -> remembered path.
    window.location.href = `${API_BASE}/tenant-portal/oauth/${provider.provider}/?redirect=${encodeURIComponent(from)}`;
  };

  const handlePasswordSignIn = async (email: string, password: string) => {
    setIsLoading(true);

    try {
      const response = await authApi.login({ email, password });
      const { access, refresh, tenant_id, site_id, is_tenant_admin, is_site_admin } = response.data;

      // Store tokens immediately so the /me/ request is authenticated
      localStorage.setItem("access_token", access);
      localStorage.setItem("refresh_token", refresh);

      // Fetch full user profile
      try {
        const meResponse = await tenantApi.getMe();
        setAuth(meResponse.data, access, refresh);
      } catch {
        // Fallback to basic info if /me/ fails
        setAuth(
          {
            id: 0,
            email,
            first_name: "",
            last_name: "",
            tenant_id,
            tenant_name: "",
            site_id,
            site_name: "",
            tenant_role: null,
            is_tenant_admin,
            is_site_admin,
            hidden_from: [],
            is_beta_tester: false,
          },
          access,
          refresh
        );
      }

      toast.success("Logged in successfully");
      navigate(from, { replace: true });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err.response?.data?.detail || "Invalid email or password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Flex mih="100vh" direction={{ base: "column", lg: "row" }}>
      {/* Left side -- information panel */}
      <Box
        visibleFrom="lg"
        w="50%" p={48}
        style={{
          background: "linear-gradient(135deg, var(--mantine-primary-color-light), var(--mantine-color-body))",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Flex direction="column" justify="center" flex={1}>
          {branding?.logo_base64 || branding?.logo_url ? (
            <img
              src={branding.logo_base64 || branding.logo_url!}
              alt={branding?.site_name || "Member Portal"}
              style={{ height: 160, width: "auto", maxWidth: 400, objectFit: "contain", marginBottom: 32 }}
            />
          ) : (
            <Title order={1} mb="md" style={{ letterSpacing: "-0.025em" }}>
              {branding?.site_name || "Member Portal"}
            </Title>
          )}
          <Text fz="lg" c="dimmed" mb="xl">
            Your office hotel management portal. Access network, services, and building information all in one place.
          </Text>
          <Stack gap="lg">
            <Group align="flex-start" gap="md">
              <Box p={10} style={{ borderRadius: 8, background: "var(--mantine-primary-color-light)" }}>
                <Wifi size={20} style={{ color: "var(--mantine-primary-color-filled)" }} />
              </Box>
              <div>
                <Text fw={600}>Network & Connectivity</Text>
                <Text fz="sm" c="dimmed">Manage your network, view connected devices, and configure static DHCP leases.</Text>
              </div>
            </Group>
            <Group align="flex-start" gap="md">
              <Box p={10} style={{ borderRadius: 8, background: "var(--mantine-primary-color-light)" }}>
                <Building2 size={20} style={{ color: "var(--mantine-primary-color-filled)" }} />
              </Box>
              <div>
                <Text fw={600}>Meeting Rooms</Text>
                <Text fz="sm" c="dimmed">Book meeting rooms, view schedules, and manage your reservations.</Text>
              </div>
            </Group>
            <Group align="flex-start" gap="md">
              <Box p={10} style={{ borderRadius: 8, background: "var(--mantine-primary-color-light)" }}>
                <Shield size={20} style={{ color: "var(--mantine-primary-color-filled)" }} />
              </Box>
              <div>
                <Text fw={600}>Secure & Private</Text>
                <Text fz="sm" c="dimmed">Your data is isolated per tenant. We follow GDPR best practices for data handling.</Text>
              </div>
            </Group>
          </Stack>
        </Flex>
        <Group gap="md">
          <Text component={Link} to="/privacy" fz="xs" c="dimmed" style={{ textDecoration: "none" }}>Privacy Policy</Text>
          <Text component={Link} to="/terms" fz="xs" c="dimmed" style={{ textDecoration: "none" }}>Terms of use</Text>
        </Group>
      </Box>

      {/* Right side -- login form */}
      <Flex
        flex={1}
        align="center"
        justify="center"
        p={{ base: "md", lg: "xl" }}
        pos="relative" style={{ background: "var(--mantine-color-body)" }}
      >
        <Card
          w="100%"
          maw={448}
          withBorder={false}
          shadow={undefined}
          style={{ border: undefined }}
          styles={{
            root: {
              border: "none",
              boxShadow: "none",
              "@media (min-width: 62em)": {
                border: "1px solid var(--mantine-color-default-border)",
                boxShadow: "var(--mantine-shadow-sm)",
              },
            },
          }}
        >
          <Stack gap="xs" ta="center" mb="md">
            {/* Mobile branding */}
            <Box hiddenFrom="lg">
              {branding?.logo_base64 || branding?.logo_url ? (
                <Flex justify="center" mb="xs">
                  <img
                    src={branding.logo_base64 || branding.logo_url!}
                    alt={branding?.site_name || "Member Portal"}
                    style={{ height: 96, width: "auto", maxWidth: 300, objectFit: "contain" }}
                  />
                </Flex>
              ) : branding?.site_name ? (
                <Text fz="lg" fw={600} mb="xs">{branding.site_name}</Text>
              ) : null}
            </Box>
            <Title order={2}>Sign in</Title>
            <Text c="dimmed" fz="sm">Access your member portal</Text>
          </Stack>

          <Stack gap="md">
            {errorParam && AUTH_ERRORS[errorParam] && (
              <Stack gap={4} ta="center">
                <Text fz="sm" c="red">{AUTH_ERRORS[errorParam]}</Text>
                {errorEmail && (
                  <Text fz="xs" c="dimmed">
                    {errorEmail}{errorProvider ? ` (via ${errorProvider})` : ""}
                  </Text>
                )}
              </Stack>
            )}

            {/* Wait for the provider list so the password form doesn't flash in
                and vanish; a failed lookup falls back to the password form. */}
            {providersLoaded || providersFailed ? (
              <LoginOptions
                providers={providers}
                passwordOpen={passwordOpen}
                onRevealPassword={() => setPasswordOpen(true)}
                onOAuthSignIn={handleOAuthSignIn}
                onPasswordSubmit={handlePasswordSignIn}
                isLoading={isLoading}
              />
            ) : (
              <Flex justify="center" py="md">
                <Loader size="sm" />
              </Flex>
            )}

            {ipData?.ip && (
              <Text fz="xs" c="dimmed" ta="center">
                Your IP: {ipData.ip}
              </Text>
            )}
          </Stack>
        </Card>

        {/* Version -- bottom right */}
        <Text
          fz={11}
          c="dimmed"
          pos="absolute" style={{ bottom: 12, right: 16 }}
          title={versionDisplay.tooltip}
        >
          {versionDisplay.text}
        </Text>

        {/* Mobile footer */}
        <Group hiddenFrom="lg" gap="sm" pos="absolute" style={{ bottom: 12, left: 16 }}>
          <Text component={Link} to="/privacy" fz={11} c="dimmed" style={{ textDecoration: "none" }}>Privacy</Text>
          <Text component={Link} to="/terms" fz={11} c="dimmed" style={{ textDecoration: "none" }}>Terms</Text>
        </Group>
      </Flex>
    </Flex>
  );
}
